import { useAnimalisContestPreview } from "@/shared/hooks/use-animalis-contest-preview"
import { isMultiPet } from "@/shared/routing/route-resolver"
import { ROUTES, ROUTE_PREFIXES } from "@/shared/routing/routes"
import { isAnimalisContestBannerVisible } from "@/shared/utils/animalis-contest"
import { newSubscriptionFlowService } from "@/store/machine"
import { cn } from "@dalmatech/ui"
import { useActor } from "@xstate/react"
import { HTMLMotionProps, motion } from "framer-motion"
import { LayoutRouterContext } from "next/dist/shared/lib/app-router-context.shared-runtime"
import { usePathname } from "next/navigation"
import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"

import * as styles from "./page-transition.styled"

type PageTransitionProps = HTMLMotionProps<"div"> & { name: string }
type PageTransitionRef = React.ForwardedRef<HTMLDivElement>

/* 
  Nextjs's app router layout isn't properly supported, or doesn't properly support framer-motion
  This is a hack to get around that
  More infos in this thread : https://github.com/vercel/next.js/issues/49279#issuecomment-1675393849
*/
function FrozenRouter(props: PropsWithChildren<{}>) {
  const context = useContext(LayoutRouterContext)
  // eslint-disable-next-line react-hooks/refs -- intentional FrozenRouter hack: freezes the first-render router context for exit animations (see link above)
  const frozen = useRef(context).current

  return (
    <LayoutRouterContext.Provider value={frozen}>{props.children}</LayoutRouterContext.Provider>
  )
}

const AddCode = ({
  setPromoCode,
  setRafCode,
}: {
  setPromoCode: Dispatch<SetStateAction<string | null | undefined>>
  setRafCode: Dispatch<SetStateAction<string | null | undefined>>
}): null => {
  const [state] = useActor(newSubscriptionFlowService)
  const statePromoCode = state.context.quote?.meta?.promo_code
  const stateRafCode = state.context.quote?.meta?.raf_code

  useEffect(() => {
    setPromoCode(localStorage.getItem("promoCode") || statePromoCode)
    setRafCode(stateRafCode)
  }, [statePromoCode, stateRafCode, setPromoCode, setRafCode])

  return null
}

function PageTransition({ children, ...rest }: PageTransitionProps, ref: PageTransitionRef) {
  const transparent = { opacity: 0 }
  const visible = { opacity: 1, transition: { delay: 0.15 } }
  const [state] = useActor(newSubscriptionFlowService)
  const pathname = usePathname()
  const isWelcomePage = pathname.startsWith(ROUTE_PREFIXES.welcome)
  const isPricingPage = pathname.startsWith(ROUTES.pricing.path)
  const isHealthPage = pathname.startsWith(ROUTES.healthCoverageParam.path)
  const [promoCode, setPromoCode] = useState<string | null | undefined>(null)
  const [rafCode, setRafCode] = useState<string | null | undefined>(null)
  const isContestPreview = useAnimalisContestPreview()
  const isRedirectIntoPrepackage =
    pathname.startsWith(ROUTES.prepackages.path) && state.context?.setLoadingPrepackage
  const isRecap = pathname.startsWith(ROUTES.recap.path)
  const isUserInfoPage = pathname.startsWith(ROUTES.userInformations.path)
  const isPrepackage = pathname.startsWith(ROUTES.prepackages.path)
  const isPrepackagesPage = pathname === ROUTES.prepackages.path
  const isHealthCoverageParamPage = pathname.includes(ROUTES.healthCoverageParam.path)
  const isMultiPetPrepackageBanner =
    (isPrepackagesPage || isHealthCoverageParamPage) && isMultiPet(state.context.quote)
  const transition = {
    duration: 0.15,
    ease: "easeOut" as const,
  }
  // Reserve the same top spacing for the Animalis contest banner (DLM-1775),
  // which reuses the promo banner slot in the Navbar.
  const isBannerDisplayed =
    Boolean(promoCode || rafCode) ||
    isAnimalisContestBannerVisible(state.context.quote, undefined, {
      forceActiveWindow: isContestPreview,
    })

  return (
    <motion.main
      ref={ref}
      initial={transparent}
      animate={visible}
      exit={transparent}
      transition={transition}
      className={cn(styles.root, rest.className)}
      {...rest}
    >
      <motion.div className={cn(styles.scroller)} layout>
        <FrozenRouter>
          {isWelcomePage ? null : <AddCode setPromoCode={setPromoCode} setRafCode={setRafCode} />}
          <motion.div
            className={cn(
              styles.container({
                isWelcomePage,
                isPricingPage,
                isBannerDisplayed,
                isRedirectIntoPrepackage,
                isPrepackage,
                isRecap,
                isHealthPage,
                isMultiPetPrepackageBanner,
                isUserInfoPage,
              })
            )}
            layout
          >
            {children}
          </motion.div>
        </FrozenRouter>
      </motion.div>
    </motion.main>
  )
}

export default forwardRef(PageTransition)
